// Localized copy for server error codes.
//
// The server emits `{ kind: 'error'; code; message }` where `message` is raw
// English (e.g. "Room is full."). The banner used to render that verbatim
// (uppercased), breaking the noir illusion in RU/UK. This maps the structured
// `code` to localized text; unknown codes fall back to the server message so we
// never show a blank banner.

import type { Lang } from '../../shared/src/types.js';

const ERROR_COPY: Record<string, Record<Lang, string>> = {
  ROOM_FULL: { ru: 'Комната заполнена', uk: 'Кімната заповнена' },
  NO_ROOM: { ru: 'Комната не найдена', uk: 'Кімнату не знайдено' },
  NO_SESSION: { ru: 'Сессия истекла — войдите заново', uk: 'Сесія завершилась — увійдіть знову' },
  GAME_IN_PROGRESS: { ru: 'Игра уже идёт — можно только вернуться', uk: 'Гра вже триває — можна лише повернутися' },
  TOO_FEW_PLAYERS: { ru: 'Нужно минимум 4 игрока (без ведущего)', uk: 'Потрібно щонайменше 4 гравці (без ведучого)' },
  NOT_ENOUGH_ROLES: { ru: 'Назначьте хотя бы по одной роли на игрока', uk: 'Призначте хоча б по одній ролі на гравця' },
  MAFIA_NO_KILLER: { ru: 'У мафии должна быть роль, способная убивать', uk: 'У мафії має бути роль, здатна вбивати' },
  MAFIA_PARITY_AT_START: { ru: 'Мафии не меньше, чем мирных — игра закончилась бы сразу', uk: 'Мафії не менше, ніж мирних — гра закінчилася б одразу' },
  LINEUP_NO_THREAT: { ru: 'Городу никто не угрожает — мирные победили бы на первом же рассвете', uk: 'Місту ніхто не загрожує — мирні перемогли б на першому ж світанку' },
  RANDOM_COUNT_MISMATCH: { ru: 'Сумма случайного состава не сходится с числом мест', uk: 'Сума випадкового складу не збігається з кількістю місць' },
  RANDOM_POOL_EXHAUSTED: { ru: 'Не хватает ролей, чтобы собрать этот состав', uk: 'Не вистачає ролей, щоб зібрати цей склад' },
  NO_PRESET: { ru: 'Неизвестный пресет', uk: 'Невідомий пресет' },
  PRESET_OUT_OF_RANGE: { ru: 'Число мест вне диапазона пресета', uk: 'Кількість місць поза діапазоном пресета' },
  NO_CHAT_ACCESS: { ru: 'Вам нельзя писать в этот канал', uk: 'Вам не можна писати в цей канал' },
  CHAT_RATE: { ru: 'Слишком часто — подождите секунду', uk: 'Занадто часто — зачекайте секунду' },
  RATE: { ru: 'Слишком часто — подождите секунду', uk: 'Занадто часто — зачекайте секунду' },
  NO_TIMER: { ru: 'Таймер не запущен', uk: 'Таймер не запущено' },
  NOT_HOST: { ru: 'Это может сделать только ведущий', uk: 'Це може зробити лише ведучий' },
  HOST_CANNOT_LEAVE: { ru: 'Ведущий не может выйти — только распустить стол', uk: 'Ведучий не може вийти — лише розпустити стіл' },
  BAD_PHASE: { ru: 'Сейчас это сделать нельзя', uk: 'Зараз це зробити не можна' },
  ROOM_LIMIT: { ru: 'Сервер перегружен — попробуйте позже', uk: 'Сервер перевантажено — спробуйте пізніше' },
  NAME_TAKEN: { ru: 'Это имя уже занято', uk: "Це ім'я вже зайняте" },
  BAD_JSON: { ru: 'Ошибка связи — повторите', uk: "Помилка зв'язку — повторіть" },
  INTERNAL: { ru: 'Внутренняя ошибка — повторите', uk: 'Внутрішня помилка — повторіть' },
};

/** Localize a server error code; falls back to the raw server message. */
export function errorMessage(code: string | undefined, t: Lang, fallback: string): string {
  if (code && ERROR_COPY[code]) return ERROR_COPY[code][t];
  return fallback;
}
